import { isAxiosError, type AxiosError, type AxiosResponse } from 'axios';
import capitalize from 'lodash.capitalize';
import { z, type ZodType } from 'zod';

// Zod transformations
export const transformOptionalString = (str?: string | null) =>
  !str?.trim() ? undefined : str;
export const transformDate = (date: string | Date) => new Date(date).toJSON();

/* Types */
export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}
export type ApiErrorDto = z.infer<typeof ApiErrorDtoSchema>;
export type ApiError = {
  message: string;
  code: number;
};
export type PagePaginationRequestDto<T = {}, SortBy = string> = {
  page?: number;
  perPage?: number;
  sortBy?: SortBy;
  sortDirection?: SortDirection;
} & T;

/* Schemas */
export const ApiErrorDtoSchema = z.object({
  error: z.string().optional(),
  message: z.union([z.string(), z.array(z.string())]),
  statusCode: z.number(),
});
export const PagePaginationResponseDtoSchema = <T>(schema: z.ZodType<T>) =>
  z.object({
    data: z.array(schema),
    meta: z.object({
      page: z.number(),
      perPage: z.number(),
      total: z.number(),
      totalPages: z.number(),
    }),
  });

/* Functions */
export function responseContract<Data>(schema: ZodType<Data>) {
  return function parseResponse(
    response: AxiosResponse<unknown>
  ): AxiosResponse<Data>['data'] {
    try {
      const parsed = schema.parse(response.data);
      return parsed;
    } catch (error) {
      console.error(
        'Response parsing error from BE to FE types:',
        error,
        response.data
      );
      return response.data as Data;
    }
  };
}
const asMessage = (value: unknown): string | string[] | null => {
  if (typeof value === 'string' && value.trim()) return value;
  if (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((v) => typeof v === 'string')
  )
    return value as string[];
  return null;
};

/**
 * Builds a usable ApiErrorDto from a response that does not match
 * `ApiErrorDtoSchema`.
 *
 * A missing or misshapen `statusCode` is no reason to throw the response away:
 * the HTTP status is on the response itself, and whatever message the server
 * did send beats a generic string. NestJS omits `statusCode` whenever an
 * HttpException is constructed from an object, so this is a routine case, not
 * an exotic one.
 *
 * Returns null only when there is no response at all — a network failure or a
 * cancelled request, where there is genuinely nothing to report.
 */
export const coerceApiErrorResponse = (err: AxiosError): ApiErrorDto | null => {
  if (!err.response) return null;

  const data: unknown = err.response.data;
  const body =
    typeof data === 'object' && data !== null
      ? (data as Record<string, unknown>)
      : {};

  // A plain-text body is worth showing; an HTML error page from a proxy or
  // load balancer is not, so fall back to the axios message for those.
  const plainText =
    typeof data === 'string' &&
    data.length <= 200 &&
    !data.trimStart().startsWith('<')
      ? asMessage(data)
      : null;

  return {
    error: typeof body.error === 'string' ? body.error : undefined,
    message: asMessage(body.message) ?? plainText ?? err.message,
    statusCode: err.response.status,
  };
};

export const normalizeApiErrorResponse = (apiError: ApiErrorDto) => {
  return {
    error: apiError.error,
    message: Array.isArray(apiError.message)
      ? apiError.message.map(capitalize).join('; ')
      : apiError.message,
    code: apiError.statusCode,
  };
};
export const getNormalizedError = (err: unknown) => {
  const isAxios = isAxiosError<ApiError>(err);

  if (!isAxios) {
    console.error(err);
  }

  const axiosError = isAxios && err.response?.data;
  const statusCode = isAxios ? err.response?.status : undefined;
  const defaultError =
    typeof err === 'string' ? err : 'An unexpected error occurred';

  return { defaultError: !isAxios && defaultError, axiosError, statusCode };
};
